import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { ReactNode, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetPropertiesForLoanDocument, PropertyFragment } from '@willow/graphql-iso/src/app';
import { addressToString } from '@willow/shared-iso';
import { CompanyId, LoanId, PropertyId } from '@willow/types-iso';

import { ControlledSelect } from './ControlledSelect';
import { WillowMultiSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  loanId: LoanId;
  isMultiSelect?: boolean;
  fieldName: FieldPathByValue<T, boolean | string | number | undefined | null>;
  control: Control<T>;
  isClearable?: boolean;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  filterFunction?: (property: PropertyFragment) => boolean;
};

interface PropertySelectOption {
  label: string;
  value: PropertyId;
}

export function ControlledLoanPropertySelect<T extends FieldValues>(props: Props<T>) {
  const {
    companyId,
    loanId,
    fieldName,
    control,
    label,
    filterFunction = () => true,
    isMultiSelect = false,
    hideErrorMessage = false,
  } = props;
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { data } = useQuery(GetPropertiesForLoanDocument, { variables: { companyId, loanId } });
  const options: PropertySelectOption[] = useMemo(
    () =>
      (data?.company.loan.currentSnapshot.configuration.properties ?? []).filter(filterFunction).map((p) => ({
        value: p.id,
        label: addressToString(p.address),
      })),
    [data?.company.loan.currentSnapshot.configuration.properties, filterFunction],
  );

  return (
    <>
      {isMultiSelect ? (
        <>
          {label && <Form.Label className="u-fs-2 fw-bold">{label}</Form.Label>}
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange, value } }) => (
              <WillowMultiSelect
                placeholder="Select properties"
                options={options}
                value={value ? options.filter((r) => value.includes(r.value)) : []}
                onChange={(properties) => {
                  onChange(properties ? properties.map((p) => p.value) : []);
                }}
              />
            )}
          />
          {!hideErrorMessage && (
            <ErrorMessage
              errors={errors}
              name={fieldName as any}
              render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
            />
          )}
        </>
      ) : (
        <ControlledSelect options={options} {...props} />
      )}
    </>
  );
}
